import { useCallback, useEffect, useState } from "react";
import { HardDrive, Loader2, RefreshCw } from "lucide-react";

import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mb = (bytes) => (bytes || 0) / 1e6;

const fmt = (bytes) => {
  const m = mb(bytes);
  if (m >= 1000) return `${(m / 1000).toFixed(2)} GB`;
  if (m >= 1) return `${m.toFixed(0)} MB`;
  return `${((bytes || 0) / 1e3).toFixed(0)} kB`;
};

/**
 * Per-tenant storage, so a filling cluster is visible here rather than in an
 * Atlas warning email. Photos are broken out because they are almost always
 * the bulk of it — Nelson alone reached 1.3GB of photo binary.
 */
export const StorageUsageCard = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/platform/storage`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.tenants || [];
  const biggest = Math.max(1, ...rows.map((r) => r.data_bytes));

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 text-slate-100 shadow-sm" data-testid="storage-usage-card">
      <div className="flex items-center gap-2">
        <HardDrive className="h-4 w-4" />
        <h3 className="text-sm font-bold uppercase tracking-wider">Storage by company</h3>
        <Button
          data-testid="storage-refresh-btn"
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-slate-300 hover:bg-slate-800 hover:text-white"
          onClick={load}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
        </Button>
      </div>

      {data ? (
        <p className="mt-2 text-sm text-slate-400" data-testid="storage-total">
          <span className="font-semibold text-slate-100">
            {fmt(data.total_data_bytes)}
          </span>{" "}
          of data across {rows.length} compan{rows.length === 1 ? "y" : "ies"} —{" "}
          {fmt(data.total_photo_bytes)} of that is photos
          {data.total_data_bytes > 0
            ? ` (${Math.round((data.total_photo_bytes / data.total_data_bytes) * 100)}%)`
            : ""}
        </p>
      ) : null}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[320px] text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-left text-[11px] uppercase tracking-wider text-slate-400">
              <th className="py-1.5 pr-2">Company</th>
              <th className="py-1.5 pr-2 text-right">Data</th>
              <th className="py-1.5 pr-2 text-right">Photos</th>
              <th className="hidden py-1.5 pr-2 text-right sm:table-cell">Jobs</th>
            </tr>
          </thead>
          <tbody data-testid="storage-rows">
            {rows.map((r) => (
              <tr key={r.slug} className="border-b border-slate-800 last:border-0">
                <td className="py-2 pr-2">
                  <div className="max-w-[120px] truncate font-medium sm:max-w-none">
                    {r.display_name}
                  </div>
                  <div className="max-w-[120px] truncate font-mono text-[10px] text-slate-400 sm:max-w-none">
                    {r.db_name}
                  </div>
                  <div className="mt-1 h-1 w-full max-w-[160px] overflow-hidden rounded-full bg-slate-800">
                    <div
                      className="h-full rounded-full bg-emerald-600"
                      style={{ width: `${(r.data_bytes / biggest) * 100}%` }}
                    />
                  </div>
                </td>
                <td className="py-2 pr-2 text-right font-mono">{fmt(r.data_bytes)}</td>
                <td className="py-2 pr-2 text-right font-mono">
                  {fmt(r.photo_bytes)}
                  <div className="text-[10px] text-slate-400">
                    {r.photo_count} file{r.photo_count === 1 ? "" : "s"}
                  </div>
                </td>
                <td className="hidden py-2 pr-2 text-right font-mono sm:table-cell">{r.jobs}</td>
              </tr>
            ))}
            {!rows.length && !loading ? (
              <tr>
                <td colSpan={4} className="py-3 text-slate-400">
                  No tenants with a database yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-xs text-slate-400">
        Data is what the documents occupy; Atlas bills on its own storage
        figure, which lags after a delete until it compacts.
      </p>
    </div>
  );
};
